import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {ChevronDown, ChevronLeft, ChevronRight, Pencil, RefreshCw, RotateCw, Send} from 'lucide-react';
import {PhoneHQLoader, PhoneHQButton} from 'ch3-ui-lib';
import {useAppDispatch} from '../../../store/store';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {
    useGetWebhookDeliveriesQuery,
    useGetWebhookDeliveryStatsQuery,
    useGetWebhookQuery,
    useRetryFailedWebhookDeliveriesMutation,
    useSendWebhookTestMutation,
    useUpdateWebhookMutation,
    type Webhook,
    type WebhookDelivery,
} from '../../../store/features/integrations/integrationsService';
import {
    dateTime,
    DELIVERY_FILTERS,
    DELIVERY_RANGES,
    type DeliveryFilter,
    deliveryError,
    deliveryFilterCount,
    endpointLabel,
    errorStatus,
    eventTypeLabel,
    isDeliveryInFlight,
    isSuccessCode,
    nextRetryLabel,
    rangeStart,
    relativeTime,
    responseText,
    shortDate,
    webhookKind,
} from './webhookModel';
import {BreadcrumbBar, DeliveryBadge, FilterPills, HealthBadge, WebhookTile} from './webhookUi';
import {DeliveryDrawer} from './DeliveryDrawer';
import {WebhookFormDrawer} from './WebhookFormDrawer';

const PAGE_SIZE = 25;
const ACTIVE_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;
const DEFAULT_RANGE_DAYS = 7;
const COLUMNS = 'grid-cols-[104px_minmax(120px,1.2fr)_minmax(130px,1fr)_64px_minmax(130px,1.4fr)_minmax(80px,0.8fr)_16px]';

export default function WebhookDeliveries() {
    const webhookId = Number(useParams<{id: string}>().id);
    const navigate = useNavigate();
    const {data: webhook, isLoading, isError} = useGetWebhookQuery({id: webhookId}, {refetchOnMountOrArgChange: true});

    return <div className="flex min-h-full flex-col">
        <BreadcrumbBar items={[{label: 'Integrations', to: '/admin/integrations'}, {label: 'Webhooks', to: '/admin/webhook/list'}, {label: webhook?.name ?? 'Delivery log'}]}/>
        {isLoading && <div className="flex justify-center py-10"><PhoneHQLoader/></div>}
        {(isError || (!isLoading && !webhook)) && <p role="alert" className="px-8 py-10 text-center text-sm text-ink-muted">This webhook could not be loaded.</p>}
        {webhook && <DeliveryLog webhook={webhook} onBack={() => navigate('/admin/webhook/list')}/>}
    </div>;
}

function DeliveryLog({webhook, onBack}: {readonly webhook: Webhook; readonly onBack: () => void}) {
    const dispatch = useAppDispatch();
    const [filter, setFilter] = useState<DeliveryFilter>('all');
    const [days, setDays] = useState<number>(DEFAULT_RANGE_DAYS);
    const [refreshedAt, setRefreshedAt] = useState(() => Date.now());
    const [offset, setOffset] = useState(0);
    const [selectedId, setSelectedId] = useState<string>();
    const [editing, setEditing] = useState(false);
    const from = useMemo(() => rangeStart(days, refreshedAt), [days, refreshedAt]);
    const statuses = DELIVERY_FILTERS.find(item => item.id === filter)?.statuses;

    const {data: page, isFetching, isError, refetch} = useGetWebhookDeliveriesQuery({id: webhook.id, statuses, from, offset, limit: PAGE_SIZE});
    const {data: stats, refetch: refetchStats} = useGetWebhookDeliveryStatsQuery({id: webhook.id, from});
    const {refetch: refetchWebhook} = useGetWebhookQuery({id: webhook.id});
    const [sendWebhookTest, {isLoading: sendingTest}] = useSendWebhookTestMutation();
    const [retryFailedDeliveries, {isLoading: retryingFailed}] = useRetryFailedWebhookDeliveriesMutation();
    const [updateWebhook, {isLoading: enabling}] = useUpdateWebhookMutation();

    const kind = webhookKind(webhook);
    const enabled = webhook.status === 'ENABLED';
    const rows = page?.data ?? [];
    const total = page?.totalCount ?? 0;
    const failedCount = stats?.failed ?? 0;
    const anyInFlight = rows.some(isDeliveryInFlight);
    const notify = (description: string, type: 'success' | 'error' = 'success') => dispatch(showToast({description, type}));

    useEffect(() => {
        const timer = window.setInterval(() => { void refetch(); void refetchStats(); void refetchWebhook(); }, anyInFlight ? ACTIVE_POLL_MS : IDLE_POLL_MS);
        return () => window.clearInterval(timer);
    }, [anyInFlight, refetch, refetchStats, refetchWebhook]);

    const changeFilter = (next: DeliveryFilter) => { setFilter(next); setOffset(0); };
    const changeRange = (next: number) => { setDays(next); setOffset(0); };

    const sendTest = async () => {
        try {
            const {delivery} = await sendWebhookTest({id: webhook.id}).unwrap();
            notify(`Test event queued for “${webhook.name}”`);
            setSelectedId(delivery.id);
        } catch (error) {
            notify(errorStatus(error) === 409 ? 'Turn the webhook on to send a test event.' : 'Test event could not be sent.', 'error');
        }
    };

    const retryFailed = async () => {
        try {
            const {queued} = await retryFailedDeliveries({id: webhook.id, since: from}).unwrap();
            notify(`${queued} ${queued === 1 ? 'delivery' : 'deliveries'} queued for retry`);
        } catch (error) {
            notify(errorStatus(error) === 409 ? 'Turn the webhook on to retry deliveries.' : 'Failed deliveries could not be retried.', 'error');
        }
    };

    const turnOn = async () => {
        try {
            await updateWebhook({id: webhook.id, data: {status: 'ENABLED'}}).unwrap();
            notify(`“${webhook.name}” turned on`);
        } catch {
            notify('Webhook could not be updated', 'error');
        }
    };

    const canRetryFailed = enabled && failedCount > 0 && !retryingFailed;

    return <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-[18px] px-5 pb-16 pt-6 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3.5">
                <button type="button" aria-label="Back to webhooks" onClick={onBack}
                    className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md border border-line bg-surface text-ink hover:bg-zinc-50">
                    <ChevronLeft size={16} className="!p-0"/>
                </button>
                <WebhookTile/>
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                        <h1 className="truncate text-xl font-semibold tracking-[-0.01em] text-ink">{webhook.name}</h1>
                        <HealthBadge kind={kind}/>
                    </div>
                    <div className="mt-0.5 truncate font-mono text-xs text-ink-muted" title={webhook.url}>
                        {endpointLabel(webhook.url)} · {webhook.eventTypes.map(eventType => eventTypeLabel(eventType.type)).join(', ')}
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap gap-2">
                <PhoneHQButton phq-id="webhookDeliveries-editBtn" variant="secondary" size="sm" onClick={() => setEditing(true)}><Pencil size={14} className="!p-0"/>Edit</PhoneHQButton>
                <PhoneHQButton phq-id="webhookDeliveries-sendTestBtn" variant="secondary" size="sm" disabled={!enabled || sendingTest} onClick={() => void sendTest()}>
                    <Send size={14} className="!p-0"/>{sendingTest ? 'Sending…' : 'Send test event'}
                </PhoneHQButton>
                <PhoneHQButton phq-id="webhookDeliveries-retryFailedBtn" variant="primary" size="sm" disabled={!canRetryFailed} onClick={() => void retryFailed()}>
                    <RotateCw size={14} className="!p-0 !text-white"/>{failedCount > 0 ? `Retry failed (${failedCount})` : 'Retry failed'}
                </PhoneHQButton>
            </div>
        </div>

        {kind === 'failing' && <div className="flex flex-wrap gap-x-5 gap-y-1.5 rounded-[10px] border border-danger-tint bg-danger-tint px-4 py-3 text-[13px] text-danger-ink">
            <span><strong className="font-semibold">Failing since</strong> {dateTime(webhook.health.failingSince)}</span>
            {webhook.health.lastError && <span><strong className="font-semibold">Last error</strong> {deliveryError(webhook.health.lastError)}</span>}
            {webhook.health.autoDisableAt && <span><strong className="font-semibold">Turns off automatically</strong> {shortDate(webhook.health.autoDisableAt)}</span>}
        </div>}

        {!enabled && <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-line bg-zinc-50 px-4 py-3">
            <p className="min-w-[240px] flex-1 text-[13.5px] text-ink-secondary">
                {kind === 'autoDisabled'
                    ? `Turned off automatically on ${shortDate(webhook.health.disabledAt)} after repeated failed deliveries. Events that happen while it’s off are not sent.`
                    : 'This webhook is turned off. Events that happen while it’s off are not sent.'}
            </p>
            <PhoneHQButton variant="secondary" size="sm" disabled={enabling} onClick={() => void turnOn()}>Turn on</PhoneHQButton>
        </div>}

        <div className="flex flex-wrap items-center gap-3">
            <FilterPills label="Filter deliveries by status" value={filter} onChange={changeFilter}
                items={DELIVERY_FILTERS.map(item => ({id: item.id, label: item.label, count: stats ? deliveryFilterCount(item.id, stats) : undefined}))}/>
            <div className="relative">
                <select aria-label="Time range" value={days} onChange={event => changeRange(Number(event.target.value))}
                    className="h-8 appearance-none rounded-md border border-line bg-surface pl-2.5 pr-8 text-[13px] text-ink outline-none focus:border-indigo-500">
                    {DELIVERY_RANGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <ChevronDown size={14} className="!p-0 pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted"/>
            </div>
            <PhoneHQButton phq-id="webhookDeliveries-refreshBtn" variant="secondary" size="sm" className="ml-auto" disabled={isFetching} onClick={() => setRefreshedAt(Date.now())}>
                <RefreshCw size={14} className="!p-0"/>Refresh
            </PhoneHQButton>
        </div>

        <div className="overflow-hidden rounded-[10px] border border-line"><div className="overflow-x-auto [contain:inline-size]"><div className="min-w-[760px]">
            <div className={`grid ${COLUMNS} items-center gap-4 border-b border-line bg-zinc-50 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted`}>
                <div>Status</div><div>Event</div><div>Created</div><div>Attempts</div><div>Last response</div><div>Next retry</div><div/>
            </div>
            {!page && isFetching && <div className="flex justify-center py-10"><PhoneHQLoader/></div>}
            {isError && <p role="alert" className="px-5 py-10 text-center text-sm text-danger-ink">Deliveries could not be loaded. Try refreshing.</p>}
            {rows.map(delivery => <DeliveryRow key={delivery.id} delivery={delivery} selected={delivery.id === selectedId} onOpen={() => setSelectedId(delivery.id)}/>)}
            {page && rows.length === 0 && !isError && <div className="flex flex-col items-center gap-1.5 px-5 py-12 text-center">
                <div className="font-semibold text-ink">No deliveries here</div>
                <div className="text-[13px] text-ink-muted">Send a test event to check that your endpoint responds.</div>
            </div>}
        </div></div></div>

        {total > PAGE_SIZE && <div className="flex items-center gap-3 text-sm text-ink-muted">
            <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
            <PhoneHQButton variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</PhoneHQButton>
            <PhoneHQButton variant="secondary" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</PhoneHQButton>
        </div>}

        {selectedId && <DeliveryDrawer key={selectedId} webhookId={webhook.id} deliveryId={selectedId} webhookEnabled={enabled} onClose={() => setSelectedId(undefined)}/>}
        {editing && <WebhookFormDrawer webhookId={webhook.id} onClose={() => setEditing(false)}/>}
    </div>;
}

function DeliveryRow({delivery, selected, onOpen}: {readonly delivery: WebhookDelivery; readonly selected: boolean; readonly onOpen: () => void}) {
    const response = responseText(delivery.lastStatusCode, delivery.lastError);
    const responseColor = isSuccessCode(delivery.lastStatusCode) ? 'text-success-ink' : response === '—' ? 'text-ink-disabled' : 'text-danger-ink';
    return <button type="button" onClick={onOpen} aria-label={`Delivery ${delivery.id}`}
        className={`box-border grid w-full ${COLUMNS} items-center gap-4 border-t border-line px-5 py-3 text-left hover:bg-zinc-50 ${selected ? 'bg-indigo-50/60' : ''}`}>
        <div><DeliveryBadge delivery={delivery}/></div>
        <div className="min-w-0">
            <div className="truncate text-[13.5px] text-ink">{eventTypeLabel(delivery.eventType)}</div>
            <div className="mt-0.5 font-mono text-[11px] text-ink-disabled">{delivery.id.slice(0, 13)}</div>
        </div>
        <div>
            <div className="text-[13.5px] text-ink">{relativeTime(delivery.createdAt)}</div>
            <div className="mt-0.5 text-[11.5px] text-ink-disabled">{dateTime(delivery.createdAt)}</div>
        </div>
        <div className="text-[13.5px] text-ink">{delivery.attempts}</div>
        <div className={`truncate text-[13.5px] ${responseColor}`}>{response}</div>
        <div className="text-[13.5px] text-ink-secondary">{nextRetryLabel(delivery)}</div>
        <ChevronRight size={16} className="!p-0 text-ink-disabled"/>
    </button>;
}
